// ============================================================================
// API - simulatorAPI.js
// ============================================================================
// ./api/simulatorAPI.js

import axiosInstance from '../utilities/axiosInstance';

const simulatorAPI = {
  // ============================================================================
  // SIMULATOR MANAGEMENT
  // ============================================================================

  // Get all simulators
  getAllSimulators: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/api/simulator', { params });
      return response.data; // {success, data, count}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get simulators: ${error.message}` };
    }
  },

  // Get simulator by sessionId
  getSimulatorBySessionId: async (sessionId) => {
    try {
      const response = await axiosInstance.get(`/api/simulator/session/${sessionId}`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { 
        success: false, 
        error: `Failed to get simulator by sessionId: ${error.message}` 
      };
    }
  },
  
  // Get simulator by ID
  getSimulatorById: async (id) => {
    try {
      const response = await axiosInstance.get(`/api/simulator/${id}`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get simulator: ${error.message}` };
    }
  },

  // Create simulator
  createSimulator: async (data) => {
    try {
      const response = await axiosInstance.post('/api/simulator', data);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to create simulator: ${error.message}` };
    }
  },

  // Update simulator
  updateSimulator: async (id, data) => {
    try {
      const response = await axiosInstance.put(`/api/simulator/${id}`, data);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to update simulator: ${error.message}` };
    }
  },

  // Delete simulator
  deleteSimulator: async (id) => {
    try {
      const response = await axiosInstance.delete(`/api/simulator/${id}`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to delete simulator: ${error.message}` };
    }
  },

  // ============================================================================
  // SIMULATOR CONTROL
  // ============================================================================

  // Start simulator
  startSimulator: async (id) => {
    try {
      const response = await axiosInstance.post(`/api/simulator/${id}/start`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to start simulator: ${error.message}` };
    }
  },

  validateSimulator: async (id) => {
    try {
      const response = await axiosInstance.post(`/api/simulator/${id}/validate`);
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Validation failed: ${error.message}` };
    }
  },

  // Stop simulator
  stopSimulator: async (id) => {
    try {
      const response = await axiosInstance.post(`/api/simulator/${id}/stop`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to stop simulator: ${error.message}` };
    }
  },

  // Pause simulator
  pauseSimulator: async (id) => {
    try {
      const response = await axiosInstance.post(`/api/simulator/${id}/pause`);
      return response.data;
    } catch (error) {
      return error?.response?.data || { 
        success: false, 
        error: `Failed to pause simulator: ${error.message}` 
      };
    }
  },

  // Resume simulator
  resumeSimulator: async (id) => {
    try {
      const response = await axiosInstance.post(`/api/simulator/${id}/resume`);
      return response.data;
    } catch (error) {
      return error?.response?.data || { 
        success: false, 
        error: `Failed to resume simulator: ${error.message}` 
      };
    }
  },

  // Reset simulator
  resetSimulator: async (id) => {
    try {
      const response = await axiosInstance.post(`/api/simulator/${id}/reset`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to reset simulator: ${error.message}` };
    }
  },

  // Get simulator status
  getSimulatorStatus: async (id) => {
    try {
      const response = await axiosInstance.get(`/api/simulator/${id}/status`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get status: ${error.message}` };
    }
  },

  // ✅ ADD THIS NEW API FUNCTION
  getActuatorStates: async (id) => {
    try {
      const response = await axiosInstance.get(`/api/simulator/${id}/actuator-states`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get actuator states: ${error.message}` };
    }
  },

  // ============================================================================
  // CODE UPLOAD
  // ============================================================================

  // Upload code to simulator
  uploadCode: async (id, code, language = 'Arduino') => {
    try {
      const response = await axiosInstance.post(`/api/simulator/${id}/code`, {
        code,
        language
      });
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to upload code: ${error.message}` };
    }
  },

  // ============================================================================
  // SENSOR OPERATIONS
  // ============================================================================

  // Attach sensor to simulator
  attachSensor: async (id, sensorData) => {
    try {
      const response = await axiosInstance.post(`/api/simulator/${id}/sensors`, sensorData);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to attach sensor: ${error.message}` };
    }
  },

  // Detach sensor from simulator
  detachSensor: async (id, sensorId) => {
    try {
      const response = await axiosInstance.delete(`/api/simulator/${id}/sensors/${sensorId}`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to detach sensor: ${error.message}` };
    }
  },

  // Read sensor value
  readSensor: async (id, sensorId) => {
    try {
      const response = await axiosInstance.get(`/api/simulator/${id}/sensors/${sensorId}/read`);
      return response.data; // {success, data: {sensorId, value, timestamp}}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to read sensor: ${error.message}` };
    }
  },

  // ============================================================================
  // GPIO OPERATIONS
  // ============================================================================

  // Write to GPIO pin
  writeGPIO: async (id, pin, value) => {
    try {
      const response = await axiosInstance.post(`/api/simulator/${id}/gpio/write`, {
        pin,
        value
      });
      return response.data; // {success, data: {pin, value}}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to write GPIO: ${error.message}` };
    }
  },

  // Read GPIO pin
  readGPIO: async (id, pin) => {
    try {
      const response = await axiosInstance.get(`/api/simulator/${id}/gpio/read`, {
        params: { pin }
      });
      return response.data; // {success, data: {pin, value}}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to read GPIO: ${error.message}` };
    }
  },

  // Configure GPIO pin
  configureGPIO: async (id, pin, mode, type) => {
    try {
      const response = await axiosInstance.post(`/api/simulator/${id}/gpio/configure`, {
        pin,
        mode,
        type
      });
      return response.data; // {success, data: {pin, mode, type}}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to configure GPIO: ${error.message}` };
    }
  },

  // ============================================================================
  // WIFI OPERATIONS
  // ============================================================================

  // Connect to WiFi
  connectWiFi: async (id, ssid, password) => {
    try {
      const response = await axiosInstance.post(`/api/simulator/${id}/wifi/connect`, {
        ssid,
        password
      });
      return response.data; // {success, data: {ssid, ipAddress, rssi}}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to connect WiFi: ${error.message}` };
    }
  },

  // Disconnect WiFi
  disconnectWiFi: async (id) => {
    try {
      const response = await axiosInstance.post(`/api/simulator/${id}/wifi/disconnect`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to disconnect WiFi: ${error.message}` };
    }
  },

  // ============================================================================
  // SERIAL OUTPUT
  // ============================================================================

  // Get serial output
  getSerialOutput: async (id, limit = 100) => {
    try {
      const response = await axiosInstance.get(`/api/simulator/${id}/serial-output`, {
        params: { limit }
      });
      return response.data; // {success, data, count}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get serial output: ${error.message}` };
    }
  },

  sendSerialInput: async (id, input) => {
    try {
      const response = await axiosInstance.post(`/api/simulator/${id}/serial-input`, {
        input  // ✅ Send directly, not wrapped in params
      });
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to send serial input: ${error.message}` };
    }
  },

  // ============================================================================
  // SIMULATORS BY FILTERS
  // ============================================================================

  // Get running simulators  
  getRunningSimulators: async () => {
    try {
      const response = await axiosInstance.get('/api/simulator/running');
      return response.data; // {success, data, count}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get running simulators: ${error.message}` };
    }
  },

  // Get active simulator count
  getActiveCount: async () => {
    try {
      const response = await axiosInstance.get('/api/simulator/active-count');
      return response.data; // {success, data: {activeCount}}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get active count: ${error.message}` };
    }
  },

  // Update sensor configuration
  updateSensorConfig: async (simulatorId, sensorId, configData) => {
    try {
      const response = await axiosInstance.put(
        `/api/simulator/${simulatorId}/sensors/${sensorId}/config`,
        configData
      );
      return response.data;
    } catch (error) {
      return error?.response?.data || { 
        success: false, 
        error: `Failed to update sensor config: ${error.message}` 
      };
    }
  },

  // ============================================================================
  // CODE GENERATION
  // ============================================================================

  mergeComponentTemplates: async (componentIds, boardType = 'ESP32', simulatorConfigs = null) => {
    try {
      const response = await axiosInstance.post('/api/simulator/code/merge', {
        componentIds,
        boardType,
        simulatorConfigs  // ✅ NEW
      });
      return response.data;
    } catch (error) {
      return error?.response?.data || { 
        success: false, 
        error: `Failed to merge component templates: ${error.message}` 
      };
    }
  },

  generateNotebookCells: async (componentIds, circuitComponents) => {
    try {
      const response = await axiosInstance.post('/api/simulator/code/notebook/generate', {
        componentIds,
        circuitComponents
      });
      return response.data;
    } catch (error) {
      return error?.response?.data || {
        success: false,
        error: `Failed to generate notebook cells: ${error.message}`
      };
    }
  },

  // Save notebook cells to simulator record (uses existing updateSimulator endpoint)
  saveNotebookCells: async (simulatorId, notebookCells) => {
    try {
      const response = await axiosInstance.put(`/api/simulator/${simulatorId}`, {
        notebookCells
      });
      return response.data;
    } catch (error) {
      return error?.response?.data || {
        success: false,
        error: `Failed to save notebook cells: ${error.message}`
      };
    }
  },

  // simulatorAPI.js — add gradingData parameter:
  saveGradingPayload: async (id, duration, gradingData = {}) => {
    try {
      const response = await axiosInstance.post(`/api/simulator/${id}/grading-payload`, {
        duration,
        ...gradingData
      });
      return response.data;
    } catch (error) {
      return error?.response?.data || {
        success: false,
        error: `Failed to save grading payload: ${error.message}`
      };
    }
  },

  submitGrades: async (id) => {
    try {
      const response = await axiosInstance.post(`/api/simulator/${id}/submit-grades`);
      return response.data;
    } catch (error) {
      return error?.response?.data || {
        success: false,
        error: `Failed to submit grades: ${error.message}`
      };
    }
  },

  addSimulationSnapshot: async (id, snapshot) => {
    try {
      const response = await axiosInstance.post(`/api/simulator/${id}/snapshot`, snapshot);
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to save snapshot: ${error.message}` };
    }
  },

  // ============================================================================
  // IIOT ENGINE CONTROL
  // Unified under /api/simulator/engine/* — parallel to nodeRedEngineAPI.js.
  // sessionId is the primary key matching the backend engine registry.
  // Both API files remain active during transition.
  // ============================================================================

  // Start the Node-RED engine for a session
  startEngine: async (sessionId) => {
    try {
      const response = await axiosInstance.post(
        `/api/simulator/engine/${sessionId}/start`
      );
      return response.data; // { success, message }
    } catch (error) {
      return error?.response?.data || {
        success: false,
        error: `Failed to start engine: ${error.message}`
      };
    }
  },

  // Stop the Node-RED engine for a session
  stopEngine: async (sessionId) => {
    try {
      const response = await axiosInstance.post(
        `/api/simulator/engine/${sessionId}/stop`
      );
      return response.data; // { success, message }
    } catch (error) {
      return error?.response?.data || {
        success: false,
        error: `Failed to stop engine: ${error.message}`
      };
    }
  },

  // Get engine running status for a session
  // returns { success, data: { running, messagesRouted, mqttConnected, startedAt? } }
  getEngineStatus: async (sessionId) => {
    try {
      const response = await axiosInstance.get(
        `/api/simulator/engine/${sessionId}/status`
      );
      return response.data;
    } catch (error) {
      return error?.response?.data || {
        success: false,
        error: `Failed to get engine status: ${error.message}`
      };
    }
  },

  // Get live IIoT data for a session (in-memory from engine tick)
  // returns { success, data: LiveData, sensors: [] }
  getEngineLiveData: async (sessionId) => {
    try {
      const response = await axiosInstance.get(
        `/api/simulator/engine/${sessionId}/live`
      );
      return response.data;
    } catch (error) {
      return error?.response?.data || {
        success: false,
        error: `Failed to get live data: ${error.message}`
      };
    }
  },

  // Generate a Node-RED function node body via AI
  // returns { success, data: { code } }
  generateNodeRedFunction: async (params) => {
    try {
      const response = await axiosInstance.post(
        '/api/simulator/engine/generate-function',
        params
      );
      return response.data;
    } catch (error) {
      return error?.response?.data || {
        success: false,
        error: `Failed to generate Node-RED function: ${error.message}`
      };
    }
  },


};

export default simulatorAPI;